import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
    useAsyncDebounce,
    useFilters,
    usePagination,
    useSortBy,
    useTable,
} from "react-table";
import axios from "axios";
import Datepicker, { DatepickerType } from "gp-datepicker";
import DualListBox from "react-dual-listbox";
import MaskedInput from "react-text-mask";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import { AlignType } from "./alignType";
import { TypeFilter } from "./typeFilter";
import { TypeOperator } from "./typeOperator";
import "gp-datepicker/dist/index.css";
import "react-dual-listbox/lib/react-dual-listbox.css";
import "./styles.scss";

export const MASK_NUMBER = (limitInteger = null) => {
    return createNumberMask({
        prefix: "",
        includeThousandsSeparator: false,
        allowDecimal: false,
        integerLimit: limitInteger,
        allowNegative: false,
        allowLeadingZeroes: true,
        requireDecimal: false,
    });
};

const If = ({ test, children }) => {
    return test ? children : null;
};

const Painel = ({ isExibe = false, titulo, subTitulo, children }) => {
    if (!isExibe) {
        return <>{children}</>;
    }

    return (
        <div className="card mb-3">
            {(titulo || subTitulo) && (
                <div className="card-header">
                    {titulo && <h4 className="mb-1">{titulo}</h4>}
                    {subTitulo && <h5 className="mb-0">{subTitulo}</h5>}
                </div>
            )}
            <div className="card-body">{children}</div>
        </div>
    );
};

const Pagination = ({
                        canNextPage,
                        canPreviousPage,
                        gotoPage,
                        previousPage,
                        nextPage,
                        pageCount,
                        pageIndex,
                    }) => {
    if (!pageCount || pageCount <= 1) {
        return null;
    }

    return (
        <nav>
            <ul className="pagination mb-0">
                <li className={`page-item ${!canPreviousPage ? "disabled" : ""}`}>
                    <button
                        type="button"
                        className="page-link"
                        onClick={() => gotoPage(0)}
                        disabled={!canPreviousPage}
                    >
                        Primeira
                    </button>
                </li>

                <li className={`page-item ${!canPreviousPage ? "disabled" : ""}`}>
                    <button
                        type="button"
                        className="page-link"
                        onClick={previousPage}
                        disabled={!canPreviousPage}
                    >
                        Anterior
                    </button>
                </li>

                <li className="page-item disabled">
          <span className="page-link">
            {pageIndex + 1} / {pageCount}
          </span>
                </li>

                <li className={`page-item ${!canNextPage ? "disabled" : ""}`}>
                    <button
                        type="button"
                        className="page-link"
                        onClick={nextPage}
                        disabled={!canNextPage}
                    >
                        Próxima
                    </button>
                </li>

                <li className={`page-item ${!canNextPage ? "disabled" : ""}`}>
                    <button
                        type="button"
                        className="page-link"
                        onClick={() => gotoPage(pageCount - 1)}
                        disabled={!canNextPage}
                    >
                        Última
                    </button>
                </li>
            </ul>
        </nav>
    );
};

const ModalConfirm = ({
                          show,
                          actionClose,
                          actionConfirm,
                          title = "Confirmação",
                          message = "Deseja confirmar esta operação?",
                      }) => {
    if (!show) {
        return null;
    }

    return (
        <div
            className="modal d-block"
            tabIndex="-1"
            role="dialog"
            style={{ background: "rgba(0, 0, 0, 0.5)" }}
        >
            <div className="modal-dialog" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{title}</h5>
                        <button type="button" className="close" onClick={actionClose}>
                            <span>&times;</span>
                        </button>
                    </div>

                    <div className="modal-body">
                        <p>{message}</p>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={actionClose}>
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-primary" onClick={actionConfirm}>
                            Confirmar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Mantido apenas como referência da versão simplificada anterior.
// eslint-disable-next-line no-unused-vars
const FormFiltro = ({ columns, onPesquisar, selectedField }) => {
    const [fieldsVisible, setFieldsVisible] = useState([]);

    const pesquisar = () => {
        onPesquisar([], selectedField ? fieldsVisible : undefined);
    };

    return (
        <div className="mb-3">
            {selectedField && (
                <div className="form-group">
                    <label>Campos visíveis</label>
                    <select
                        multiple
                        className="form-control"
                        value={fieldsVisible}
                        onChange={(event) => {
                            const values = Array.from(event.target.selectedOptions).map(
                                (option) => option.value
                            );
                            setFieldsVisible(values);
                        }}
                    >
                        {columns
                            .filter((column) => column.Header)
                            .map((column) => (
                                <option key={column.Header} value={column.Header}>
                                    {column.Header}
                                </option>
                            ))}
                    </select>
                </div>
            )}

            <button type="button" className="btn btn-primary btn-sm" onClick={pesquisar}>
                Pesquisar
            </button>
        </div>
    );
};

const getOperatorDefault = (tipo) => {
    switch (tipo) {
        case TypeFilter.TEXT:
            return TypeOperator.CONTEM;
        case TypeFilter.NUMBER:
        case TypeFilter.DEFAULT:
        case TypeFilter.BOOLEAN:
            return TypeOperator.IGUAL;
        case TypeFilter.DATE:
            return TypeOperator.ENTRE;
        default:
            return undefined;
    }
};

const buildFilterFromColumn = (column) => {
    if (!column || !column.filter) {
        return undefined;
    }

    return {
        ...column.filter,
        id: column.accessor,
        field: column.filter.field || column.accessor,
    };
};

const FormFiltroAvancado = ({ columns, onPesquisar, selectedField, classes }) => {
    const filterColumns = useMemo(
        () => columns.filter((column) => column.filter),
        [columns]
    );
    const [filtros, setFiltros] = useState([]);
    const [filtro, setFiltro] = useState(() => buildFilterFromColumn(filterColumns[0]));
    const [fieldsVisible, setFieldsVisible] = useState([]);
    const [isMostraPicklist, setMostraPicklist] = useState(false);

    useEffect(() => {
        if (columns) {
            setFieldsVisible(
                columns
                    .filter((column) => column.Header && (column.isVisible || column.isVisible === undefined))
                    .map((column) => column.Header)
            );
        }
    }, [columns]);

    useEffect(() => {
        if (!filtro && filterColumns.length > 0) {
            setFiltro(buildFilterFromColumn(filterColumns[0]));
        }
    }, [filterColumns, filtro]);

    const procurarFilter = (id) => {
        const column = columns.find((item) => item.accessor === id);
        return column ? column.filter : null;
    };

    const adicionarFiltro = () => {
        if (!filtro || filtros.some((item) => item.id === filtro.id)) {
            return;
        }

        const filter = procurarFilter(filtro.id);
        const novoFiltro = {
            ...filtro,
            checked: true,
            field: filtro.field || filtro.id,
            mask: filter ? filter.mask : null,
            valueFilterApi: filter ? filter.valueFilterApi : null,
            operator: filtro.operator || getOperatorDefault(filtro.tipo),
            value: filtro.value || "",
            valueIni: filtro.valueIni || "",
            valueEnd: filtro.valueEnd || "",
        };

        setFiltros([...filtros, novoFiltro]);
    };

    const limparFiltro = () => {
        setFiltros([]);

        if (onPesquisar) {
            onPesquisar([], fieldsVisible);
        }
    };

    const atualizarFiltro = (item, values) => {
        setFiltros((current) =>
            current.map((filtroAtual) =>
                filtroAtual.id === item.id ? { ...filtroAtual, ...values } : filtroAtual
            )
        );
    };

    const onChangeValor = (event, item) => {
        const valor = event.target.value;
        atualizarFiltro(item, {
            value: item.valueFilterApi ? item.valueFilterApi(valor) : valor,
        });
    };

    const onChangeValorIni = (event, item) => {
        const valor = event.target.value;
        atualizarFiltro(item, {
            valueIni: item.valueFilterApi ? item.valueFilterApi(valor) : valor,
        });
    };

    const onChangeValorEnd = (event, item) => {
        const valor = event.target.value;
        atualizarFiltro(item, {
            valueEnd: item.valueFilterApi ? item.valueFilterApi(valor) : valor,
        });
    };

    const onChangeOperator = (event, item) => {
        atualizarFiltro(item, {
            operator: event.target.value,
            value: "",
            valueIni: "",
            valueEnd: "",
        });
    };

    const onChangeCustomCombobox = (event, item) => {
        const opcao = JSON.parse(event.target.value);

        atualizarFiltro(item, {
            operator: opcao.operator || item.operator,
            tipo: opcao.tipo || item.tipo,
            value: opcao.value,
        });
    };

    const onChangeSelect = (event) => {
        const column = filterColumns.find((item) => item.accessor === event.target.value);
        setFiltro(buildFilterFromColumn(column));
    };

    const remover = (event, item) => {
        event.preventDefault();
        setFiltros(filtros.filter((filtroAtual) => item.id !== filtroAtual.id));
    };

    const mostrarPicklist = (event) => {
        event.preventDefault();
        setMostraPicklist(!isMostraPicklist);
    };

    const pesquisar = (event) => {
        event.preventDefault();

        if (onPesquisar) {
            onPesquisar(
                filtros.filter(
                    (filter) =>
                        filter.checked &&
                        ((filter.value !== undefined && filter.value !== null && filter.value !== "") ||
                            (filter.valueIni !== undefined &&
                                filter.valueIni !== null &&
                                filter.valueIni !== "" &&
                                filter.valueEnd !== undefined &&
                                filter.valueEnd !== null &&
                                filter.valueEnd !== ""))
                ),
                fieldsVisible
            );
        }
    };

    const fieldOptions = columns
        .filter((column) => column.Header)
        .map((column) => ({
            value: column.Header,
            label: column.Header,
        }));

    return (
        <>
            <div className={classes || "row"}>
                <div className="form-group col-4 col-md-3 col-lg-3 col-xl-2 input-group-sm">
                    <label htmlFor="filtro" className="col-form-label">
                        Filtro
                    </label>
                    <select
                        id="filtro"
                        className="custom-select"
                        value={filtro ? filtro.id : ""}
                        onChange={onChangeSelect}
                    >
                        {filterColumns.map((column) => (
                            <option key={column.accessor} value={column.accessor}>
                                {column.filter.nome}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="btn-group-vertical">
                    <button
                        type="button"
                        className="btn btn-icon-info btn-sm"
                        onClick={adicionarFiltro}
                        title="Adicionar Filtro"
                    >
                        <i className="fas fa-plus" />
                    </button>
                </div>

                <div className="btn-group-vertical ml-3">
                    <If test={selectedField}>
                        <button
                            type="button"
                            className="btn btn-icon-info btn-sm"
                            onClick={mostrarPicklist}
                            title="Organizar as colunas"
                        >
                            <i className="fas fa-table" />
                        </button>
                    </If>
                </div>
            </div>

            <div className="row">
                <If test={filtros.length > 0}>
                    {filtros.map((item, index) => (
                        <Fragment key={item.id}>
                            <div className="row col-md-12">
                                <div className="col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3 col-form-group input-group-sm">
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={(event) => remover(event, item)}
                                        title="Excluir Filtro"
                                    >
                                        <i className="fas fa-minus" />
                                    </button>
                                    <label className="control-label ml-3">{item.nome}</label>
                                </div>

                                <If test={item.tipo !== TypeFilter.DATE && item.checked}>
                                    <div className="col-md-3 col-lg-3 col-xl-2 form-group input-group-sm">
                                        {item.tipo === TypeFilter.DEFAULT && !item.isCustomCombobox && (
                                            <TipoFiltroCombobox index={index} item={item} onChange={onChangeOperator} />
                                        )}
                                        {item.tipo === TypeFilter.TEXT && !item.isCustomCombobox && (
                                            <TipoFiltroTexto index={index} item={item} onChange={onChangeOperator} />
                                        )}
                                        {item.tipo === TypeFilter.NUMBER && !item.isCustomCombobox && (
                                            <TipoFiltroNumber index={index} item={item} onChange={onChangeOperator} />
                                        )}
                                        {item.tipo === TypeFilter.BOOLEAN && !item.isCustomCombobox && (
                                            <TipoFiltroBoolean index={index} item={item} onChange={onChangeOperator} />
                                        )}
                                        {item.isCustomCombobox && (
                                            <TipoFiltroCustomCombobox
                                                index={index}
                                                item={item}
                                                onChange={onChangeCustomCombobox}
                                            />
                                        )}
                                    </div>

                                    <If test={item.operator === TypeOperator.ENTRE && !item.isCustomCombobox}>
                                        <div className="col-md-6 col-lg-6 col-xl-3 form-group input-group-sm">
                                            <MaskedInput
                                                name={item.field}
                                                id={`${item.field}Ini`}
                                                className="form-control"
                                                guide={false}
                                                mask={item.mask ? item.mask : MASK_NUMBER()}
                                                value={item.valueIni}
                                                onChange={(event) => onChangeValorIni(event, item)}
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="col-md-6 col-lg-6 col-xl-3 form-group input-group-sm">
                                            <MaskedInput
                                                name={item.field}
                                                id={`${item.field}End`}
                                                className="form-control"
                                                value={item.valueEnd}
                                                onChange={(event) => onChangeValorEnd(event, item)}
                                                guide={false}
                                                mask={item.mask ? item.mask : MASK_NUMBER()}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </If>

                                    <If test={item.operator !== TypeOperator.ENTRE && !item.isCustomCombobox}>
                                        <If test={item.tipo !== TypeFilter.NUMBER}>
                                            <div className="col-md-6 col-lg-6 col-xl-3 form-group input-group-sm">
                                                <input
                                                    name={item.field}
                                                    id={item.field}
                                                    className="form-control"
                                                    value={item.value || ""}
                                                    onChange={(event) => onChangeValor(event, item)}
                                                />
                                            </div>
                                        </If>
                                        <If test={item.tipo === TypeFilter.NUMBER}>
                                            <div className="col-md-6 col-lg-6 col-xl-3 form-group input-group-sm">
                                                <MaskedInput
                                                    name={item.field}
                                                    id={item.field}
                                                    className="form-control"
                                                    guide={false}
                                                    mask={item.mask ? item.mask : MASK_NUMBER()}
                                                    onChange={(event) => onChangeValor(event, item)}
                                                    value={item.value}
                                                    autoComplete="off"
                                                />
                                            </div>
                                        </If>
                                    </If>
                                </If>

                                <If test={item.tipo === TypeFilter.DATE && item.checked && !item.isCustomCombobox}>
                                    <div className="row col-md-9 form-group input-group-sm">
                                        <TipoFiltroDate item={item} onChange={atualizarFiltro} />
                                    </div>
                                </If>
                            </div>
                        </Fragment>
                    ))}
                </If>

                <If test={isMostraPicklist}>
                    <div className="row col-md-12">
                        <div className="col-md-12">
                            <h2 className="legend">Seleção de Campos</h2>
                            <div className="linha-legenda" />
                            <div className="col-md-12 col-lg-6 col-xl-6">
                                <DualListBox
                                    options={fieldOptions}
                                    selected={fieldsVisible}
                                    onChange={setFieldsVisible}
                                />
                            </div>
                        </div>
                    </div>
                </If>

                <If test={filtros.length > 0 || isMostraPicklist}>
                    <div className="row col-md-12 button-right mt-4 ml-0 mb-4">
                        <button
                            type="button"
                            className="btn btn-outline-dark btn-sm mr-3"
                            onClick={limparFiltro}
                        >
                            <i className="fas fa-eraser" />
                            Limpar
                        </button>
                        <button type="button" className="btn btn-info btn-sm" onClick={pesquisar}>
                            <i className="fas fa-search" />
                            Pesquisar
                        </button>
                    </div>
                </If>
            </div>
        </>
    );
};

const TipoFiltroCustomCombobox = ({ index, item, onChange }) => {
    return (
        <select
            id={`tipoFiltroCustomCombobox${index}`}
            className="custom-select"
            onChange={(event) => onChange(event, item)}
        >
            {item.opcoes.map((opcao, optionIndex) => (
                <option key={optionIndex} value={JSON.stringify(opcao)}>
                    {opcao.nome}
                </option>
            ))}
        </select>
    );
};

const TipoFiltroCombobox = ({ index, item, onChange }) => {
    return (
        <select
            id={`tipo${index}`}
            className="custom-select"
            value={item.operator}
            onChange={(event) => onChange(event, item)}
        >
            <option value={TypeOperator.IGUAL}>igual</option>
            <option value={TypeOperator.DIFERENTE}>diferente de</option>
        </select>
    );
};

const TipoFiltroTexto = ({ index, item, onChange }) => {
    return (
        <select
            id={`tipo${index}`}
            className="custom-select"
            value={item.operator}
            onChange={(event) => onChange(event, item)}
        >
            <option value={TypeOperator.CONTEM}>contém</option>
            <option value={TypeOperator.NAO_CONTEM}>não contém</option>
        </select>
    );
};

const TipoFiltroNumber = ({ index, item, onChange }) => {
    return (
        <select
            id={`tipo${index}`}
            className="custom-select"
            value={item.operator}
            onChange={(event) => onChange(event, item)}
        >
            <option value={TypeOperator.IGUAL}>igual a</option>
            <option value={TypeOperator.MAIOR_OU_IGUAL_QUE}>maior ou igual a</option>
            <option value={TypeOperator.MENOR_OU_IGUAL_QUE}>menor ou igual a</option>
            <option value={TypeOperator.ENTRE}>entre</option>
        </select>
    );
};

const TipoFiltroBoolean = ({ index, item, onChange }) => {
    return (
        <select
            id={`tipo${index}`}
            className="custom-select"
            value={item.operator}
            onChange={(event) => onChange(event, item)}
        >
            <option value={TypeOperator.IGUAL}>igual</option>
            <option value={TypeOperator.DIFERENTE}>diferente de</option>
        </select>
    );
};

const TipoFiltroDate = ({ item, onChange }) => {
    const openCalendarInicial = (date) => {
        onChange(item, {
            valueIni: item.valueFilterApi ? item.valueFilterApi(date) : date,
        });
    };

    const openCalendarFinal = (date) => {
        onChange(item, {
            valueEnd: item.valueFilterApi ? item.valueFilterApi(date) : date,
        });
    };

    return (
        <>
            <Datepicker
                id={`${item.field}DateInicial`}
                date={item.dataInicial}
                typeDatepicker={item.isTime ? DatepickerType.DATE_TIME : DatepickerType.DATE}
                classes="col-6 col-lg-6 col-xl-4"
                callback={openCalendarInicial}
                isTime={item.isTime !== undefined ? item.isTime : false}
            />
            <Datepicker
                id={`${item.field}DateFinal`}
                date={item.dataFinal}
                typeDatepicker={item.isTime ? DatepickerType.DATE_TIME : DatepickerType.DATE}
                classes="col-6 col-lg-6 col-xl-4"
                callback={openCalendarFinal}
                isTime={item.isTime !== undefined ? item.isTime : false}
            />
        </>
    );
};

const DefaultColumnFilter = ({
                                 column: { filterValue = [], setFilter, filter },
                             }) => {
    const onChange = (event, isNumber = false) => {
        event.preventDefault();

        const value = event.target.value;
        const minLength = filter && filter.minLength ? filter.minLength : undefined;
        const operator = filter && filter.operator ? filter.operator : TypeOperator.DEFAULT;
        const tipo = filter && filter.tipo ? filter.tipo : TypeFilter.DEFAULT;

        if (filter && (minLength || operator || tipo)) {
            if (isNumber) {
                const valueFilterApi = filter && filter.valueFilterApi ? filter.valueFilterApi : undefined;
                setFilter([value, minLength, operator, tipo, valueFilterApi]);
            } else {
                setFilter([value, minLength, operator, tipo]);
            }
        } else {
            setFilter(value || undefined);
        }
    };

    return (
        <>
            {(!filter || filter.tipo !== TypeFilter.NUMBER) && (
                <input
                    type="text"
                    className="form-control input-sm form-table"
                    value={
                        filter && (filter.minLength || filter.operator || filter.tipo)
                            ? filterValue[0] || ""
                            : filterValue || ""
                    }
                    onChange={onChange}
                />
            )}

            {filter && filter.tipo === TypeFilter.NUMBER && (
                <MaskedInput
                    className="form-control input-sm form-table"
                    guide={false}
                    mask={filter.mask ? filter.mask : MASK_NUMBER()}
                    onChange={(event) => onChange(event, true)}
                    value={
                        filter && (filter.minLength || filter.operator || filter.tipo)
                            ? filterValue[0] || ""
                            : filterValue || ""
                    }
                    autoComplete="off"
                />
            )}
        </>
    );
};

const ColumnActions = (
    editAction,
    openModal,
    deleteAction,
    hasPermissionsAction,
    hasPermissionEditar,
    hasPermissionExcluir,
    editConditions,
    deleteConditions,
    hasPermission
) => {
    const getTypeString = (condition) => typeof condition === "string";

    const canShowActions =
        !hasPermission || hasPermission({ hasAnyRole: hasPermissionsAction });

    if (canShowActions) {
        return {
            Header: "Ações",
            accessor: "id",
            disableFilters: true,
            disableSortBy: true,
            isButtonResetAllFilters: true,
            width: "10%",
            Cell: ({ row, value }) => {
                const conditionEdit = getTypeString(editConditions)
                    ? row.original[editConditions]
                    : editConditions;

                const conditionDelete = getTypeString(deleteConditions)
                    ? row.original[deleteConditions]
                    : deleteConditions;

                const canEdit =
                    !hasPermission || hasPermission({ hasAnyRole: hasPermissionEditar });

                const canDelete =
                    !hasPermission || hasPermission({ hasAnyRole: hasPermissionExcluir });

                return (
                    <div className="cell-action text-center">
                        {editAction && canEdit && conditionEdit && (
                            <button
                                type="button"
                                className="btn btn-outline-success btn-sm"
                                title="Editar"
                                onClick={(event) => editAction(event, value)}
                            >
                                <i className="fas fa-edit" />
                            </button>
                        )}

                        {deleteAction && canDelete && conditionDelete && (
                            <button
                                type="button"
                                className="btn btn-outline-danger btn-sm ml-2"
                                title="Excluir"
                                onClick={(event) => openModal(event, value)}
                            >
                                <i className="fas fa-trash" />
                            </button>
                        )}
                    </div>
                );
            },
        };
    }

    return {
        Header: "Id",
        accessor: "id",
        disableFilters: true,
        disableSortBy: true,
        isVisible: false,
    };
};

export const Table = ({
                          columns,
                          data = [],
                          editAction,
                          deleteAction,
                          editConditions = true,
                          deleteConditions = true,
                          initialPage = 0,
                          url,
                          isPaginationMemory = false,
                          isFilterTable = false,
                          hasPermissionsAction,
                          hasPermissionEditar,
                          hasPermissionExcluir,
                          alignPagination = AlignType.CENTER,
                          isExibePainel = false,
                          tituloPainelFiltros,
                          tituloPainelTable,
                          tituloPage,
                          isFormFilter = false,
                          filtroLista = {},
                          updateTable = false,
                          resetTable = false,
                          selectedField = false,
                          hasPermission,
                          message = {},
                      }) => {
    const itensPerPage = [5, 10, 20, 30, 40, 50];

    const [showModal, setShowModal] = useState(false);
    const [controlledPageCount, setControlledPageCount] = useState(
        isPaginationMemory ? data.length : 0
    );
    const [controlledData, setControlledData] = useState(data);
    const [totalElements, setTotalElements] = useState(0);
    const [item, setItem] = useState({});
    const [resetFilterNotExecuted, setResetFilterNotExecuted] = useState(false);
    const [formFilterQueries, setFormFilterQueries] = useState([]);

    const openModal = useCallback((event, selectedItem) => {
        if (event) {
            event.preventDefault();
        }

        setShowModal(true);
        setItem(selectedItem);
    }, []);

    const closeModal = useCallback(() => {
        setShowModal(false);
        setItem({});
    }, []);

    const tableColumns = useMemo(() => {
        const newColumns = [...columns];

        if (deleteAction || editAction) {
            const exists = newColumns.some(
                (column) => column.Header === "Ações" && column.accessor === "id"
            );

            if (!exists) {
                newColumns.push(
                    ColumnActions(
                        editAction,
                        openModal,
                        deleteAction,
                        hasPermissionsAction,
                        hasPermissionEditar,
                        hasPermissionExcluir,
                        editConditions,
                        deleteConditions,
                        hasPermission
                    )
                );
            }
        }

        return newColumns;
    }, [
        columns,
        deleteAction,
        editAction,
        hasPermissionsAction,
        hasPermissionEditar,
        hasPermissionExcluir,
        editConditions,
        deleteConditions,
        hasPermission,
        openModal,
    ]);

    const defaultColumn = useMemo(
        () => ({
            Filter: isFilterTable ? DefaultColumnFilter : "",
        }),
        [isFilterTable]
    );

    useEffect(() => {
        if (data.length > 0) {
            setControlledPageCount(data.length);
            setControlledData(data);
        }
    }, [data]);

    const getLista = (requestUrl, params) => {
        axios.post(requestUrl, params).then((response) => {
            const dados = response ? response.data : response;

            if (dados) {
                setControlledPageCount(dados.totalPages);
                setControlledData(dados.content);
                setTotalElements(dados.totalElements || 0);
            }
        });
    };

    const hasPodePesquisar = (filter) => {
        if (filter.value[0] === "") {
            return true;
        }

        if (filter.value[0].length < filter.value[1]) {
            return false;
        }

        return true;
    };

    const obterLista = (pageIndexParam, pageSizeParam, requestUrl, sorted, filtros = [], resetFilter) => {
        const reset = resetFilter ? resetFilter : !resetFilterNotExecuted;

        if (requestUrl && reset) {
            const pagination = {
                page: pageIndexParam,
                size: pageSizeParam,
            };

            let searchQueries = [];
            const isPodePesquisar = [];

            if (filtros.length > 0 || resetFilter) {
                setResetFilterNotExecuted(filtros.length > 0);
                searchQueries = filtros;
                setAllFilters([]);
            } else if (filters.length > 0) {
                setResetFilterNotExecuted(false);

                filters.forEach((filter) => {
                    let valor;
                    let operator;
                    let tipo;

                    if (Array.isArray(filter.value)) {
                        isPodePesquisar.push(hasPodePesquisar(filter));

                        const valueFilterApi = filter.value[4];
                        valor = valueFilterApi ? valueFilterApi(filter.value[0]) : filter.value[0];
                        operator = filter.value[2] ? filter.value[2] : TypeOperator.DEFAULT;
                        tipo = filter.value[3] ? filter.value[3] : TypeFilter.DEFAULT;
                    } else {
                        valor = filter.value;
                        operator = TypeOperator.DEFAULT;
                        tipo = TypeFilter.DEFAULT;
                    }

                    if (valor || valor === false || valor === 0) {
                        searchQueries.push({
                            operator,
                            value: valor,
                            valueIni: null,
                            valueEnd: null,
                            field: filter.id,
                            fieldType: tipo,
                        });
                    }
                });
            } else if (formFilterQueries.length > 0) {
                setResetFilterNotExecuted(true);
                searchQueries = formFilterQueries;
            }

            if (sorted.length > 0) {
                const tipoSorted = sorted && sorted[0].desc ? "desc" : "asc";
                pagination.sort = `${sorted[0].id},${tipoSorted}`;
            }

            if (isPodePesquisar.length === 0 || isPodePesquisar.includes(true)) {
                const paramsRequisicao = {
                    pagination,
                    searchQueries,
                };

                if (filtroLista && Object.keys(filtroLista).length > 0) {
                    paramsRequisicao.searchQueries.push(filtroLista);
                }

                getLista(requestUrl, paramsRequisicao);
            }
        } else {
            setResetFilterNotExecuted(false);
        }
    };

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        setAllFilters,
        setHiddenColumns,
        state: { pageIndex, pageSize, sortBy, filters },
    } = useTable(
        {
            columns: tableColumns,
            data: controlledData,
            initialState: { pageIndex: initialPage },
            manualPagination: !isPaginationMemory,
            manualSortBy: !isPaginationMemory,
            manualFilters: !isPaginationMemory,
            pageCount: controlledPageCount,
            autoResetPage: false,
            disableMultiSort: true,
            defaultColumn,
        },
        useFilters,
        useSortBy,
        usePagination
    );

    const onFetchDataDebounce = useAsyncDebounce(obterLista, 100);

    const definirColumns = (fieldsVisible) => {
        tableColumns.forEach((column) => {
            if (column.Header) {
                column.isVisible = fieldsVisible ? fieldsVisible.includes(column.Header) : true;
            }
        });

        setHiddenColumns(
            tableColumns
                .filter((column) =>
                    column.isVisible !== undefined ? !column.isVisible : false
                )
                .map((column) => column.accessor)
        );
    };

    const onPesquisar = (filtros, fieldsVisible) => {
        if (selectedField) {
            definirColumns(fieldsVisible);
        }

        setResetFilterNotExecuted(false);

        const searchQueries = filtros.map((filtro) => {
            const searchQuery = {
                operator: filtro.operator,
                field: filtro.field,
                fieldType: filtro.tipo,
            };

            if (filtro.operator === TypeOperator.ENTRE || filtro.tipo === TypeFilter.DATE) {
                searchQuery.valueIni = filtro.valueIni;
                searchQuery.valueEnd = filtro.valueEnd;
            } else {
                searchQuery.value = filtro.value;
            }

            return searchQuery;
        });

        setFormFilterQueries(searchQueries);

        obterLista(initialPage, pageSize, url, sortBy, searchQueries, true);
    };

    useEffect(() => {
        if (!isPaginationMemory) {
            onFetchDataDebounce(pageIndex, pageSize, url, sortBy);
        }
    }, [onFetchDataDebounce, pageIndex, pageSize, url, sortBy, filters, isPaginationMemory]);

    useEffect(() => {
        setHiddenColumns(
            tableColumns
                .filter((column) =>
                    column.isVisible !== undefined ? !column.isVisible : false
                )
                .map((column) => column.accessor)
        );
    }, [setHiddenColumns, tableColumns]);

    useEffect(() => {
        if (resetTable) {
            setControlledPageCount(0);
            setControlledData([]);
        }
    }, [resetTable]);

    useEffect(() => {
        if (updateTable) {
            obterLista(pageIndex, pageSize, url, sortBy);
        }
    }, [updateTable]); // eslint-disable-line

    const getClassAlignPagination = () => {
        switch (alignPagination) {
            case AlignType.CENTER:
                return "pagination-center";
            case AlignType.LEFT:
                return "pagination-left";
            case AlignType.RIGTH:
                return "pagination-right";
            default:
                return AlignType.DEFAULT;
        }
    };

    const getClassTd = (align) => {
        switch (align) {
            case AlignType.LEFT:
                return "text-left";
            case AlignType.RIGTH:
                return "text-right";
            case AlignType.CENTER:
                return "text-center";
            default:
                return "";
        }
    };

    const resetFilters = () => setAllFilters([]);

    const actionDelete = async (selectedItem) => {
        if (deleteAction) {
            await deleteAction(selectedItem, obterLista, initialPage, pageSize, url, sortBy);
            closeModal();
        }
    };

    const generateReport = () => {
        if (!url) {
            return;
        }

        const pagination = {
            page: pageIndex,
            size: pageSize,
        };

        const searchQueries = [...formFilterQueries];

        if (sortBy.length > 0) {
            const tipoSorted = sortBy && sortBy[0].desc ? "desc" : "asc";
            pagination.sort = `${sortBy[0].id},${tipoSorted}`;
        }

        if (filtroLista && Object.keys(filtroLista).length > 0) {
            const jaExiste = searchQueries.some(
                (query) => JSON.stringify(query) === JSON.stringify(filtroLista)
            );

            if (!jaExiste) {
                searchQueries.push(filtroLista);
            }
        }

        const paramsRequisicao = {
            pagination,
            searchQueries,
        };

        axios
            .post(`${url}/report`, paramsRequisicao, { responseType: "blob" })
            .then((response) => {
                const reportUrl = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = reportUrl;
                link.setAttribute("download", "auditoria_report.pdf");
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
            })
            .catch(() => {
                alert("Erro ao gerar relatório. Tente novamente.");
            });
    };

    return (
        <>
            <If test={isFormFilter}>
                <Painel
                    isExibe={isExibePainel}
                    titulo={tituloPage}
                    subTitulo={tituloPainelFiltros}
                >
                    <FormFiltroAvancado
                        columns={tableColumns}
                        onPesquisar={onPesquisar}
                        selectedField={selectedField}
                    />
                </Painel>
            </If>

            <Painel isExibe={isExibePainel} subTitulo={tituloPainelTable}>
                <div>
                    <div className="view form-group row mr-0">
                        <div className="col-md-6 d-flex align-items-center">
                            <label className="mb-0">
                                <strong>Total:</strong> {totalElements}
                            </label>
                        </div>

                        <div className="col-md-6 d-flex align-items-center justify-content-end">
                            <label htmlFor="visualizar" className="mr-3 col-form-label">
                                Visualizar
                            </label>
                            <select
                                id="visualizar"
                                className="custom-select"
                                style={{ width: "auto" }}
                                value={pageSize}
                                onChange={(event) => {
                                    setPageSize(Number(event.target.value));
                                }}
                            >
                                {itensPerPage.map((itemPageSize) => (
                                    <option key={itemPageSize} value={itemPageSize}>
                                        {itemPageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="table-responsive">
                    <table
                        {...getTableProps()}
                        className="table table-sm table-bordered table-align table-striped"
                    >
                        <thead className="thead-light">
                        {headerGroups.map((headerGroup, index) => (
                            <Fragment key={index}>
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column) => (
                                        <th
                                            className="text-center"
                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                            width={column.width ? column.width : ""}
                                            scope="col"
                                        >
                                            {column.render("Header")}{" "}
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <i className="fas fa-caret-up" />
                                                ) : (
                                                    <i className="fas fa-caret-down" />
                                                )
                                            ) : (
                                                ""
                                            )}
                                        </th>
                                    ))}
                                </tr>

                                {isFilterTable && (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map((column) => (
                                            <th
                                                {...column.getHeaderProps()}
                                                className="text-center"
                                                scope="col"
                                            >
                                                {column.canFilter ? column.render("Filter") : null}
                                                {column.isButtonResetAllFilters ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-dark btn-sm btn-reset"
                                                        title="Limpar Filtros"
                                                        onClick={resetFilters}
                                                    >
                                                        <i className="fas fa-eraser" />
                                                    </button>
                                                ) : null}
                                            </th>
                                        ))}
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                        </thead>

                        <tbody {...getTableBodyProps()}>
                        {page.length > 0 ? (
                            page.map((row) => {
                                prepareRow(row);

                                return (
                                    <tr {...row.getRowProps()}>
                                        {row.cells.map((cell) => (
                                            <td
                                                {...cell.getCellProps()}
                                                className={getClassTd(cell.column.alignItems)}
                                            >
                                                {cell.render("Cell")}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={tableColumns.length} className="noResult">
                                    Nenhum registro encontrado
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                {url && (
                    <div
                        style={{
                            marginBottom: "20px",
                            display: "flex",
                            justifyContent: "flex-start",
                        }}
                    >
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            title="Gerar Relatório"
                            onClick={generateReport}
                        >
                            <i className="fas fa-file-pdf mr-1" />
                            Gerar Relatório
                        </button>
                    </div>
                )}

                <div className={`pagination-table ${getClassAlignPagination()}`}>
                    <Pagination
                        canNextPage={canNextPage}
                        canPreviousPage={canPreviousPage}
                        gotoPage={gotoPage}
                        previousPage={previousPage}
                        nextPage={nextPage}
                        pageCount={pageCount}
                        pageIndex={pageIndex}
                        pageOptions={pageOptions}
                    />
                </div>
            </Painel>

            <ModalConfirm
                show={showModal}
                actionClose={closeModal}
                message={message.CONFIRMACAO_EXCLUSAO}
                title={message.TITLE_CONFIRMACAO}
                actionConfirm={() => actionDelete(item)}
            />
        </>
    );
};
